import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ExpenseApiService } from '../../../core/api-services/expense-api.service';
import { ScholarshipApiService } from '../../../core/api-services/scholarship-api.service';
import { AuthorizationService } from '../../../core/authorization/authorization.service';
import { Permissions } from '../../../core/authorization/permissions';
import { BenefitPayment, BenefitPaymentStatus } from '../../../core/models/expense';
import { Scholarship } from '../../../core/models/scholarship';

/**
 * Administrator view of recurring benefit payments.
 *
 * These are the expenses paid on the student's behalf (accommodation,
 * allowances, health insurance), including payments made directly to a service
 * provider. Settling a payment is what consumes the benefit's budget.
 */
@Component({
  selector: 'app-admin-expenses',
  standalone: true,
  imports: [CommonModule, DatePipe, ReactiveFormsModule],
  templateUrl: './expenses.html',
  styleUrl: './expenses.scss',
})
export class AdminExpensesComponent implements OnInit {
  private readonly api = inject(ExpenseApiService);
  private readonly scholarshipApi = inject(ScholarshipApiService);
  private readonly fb = inject(FormBuilder);
  readonly authorization = inject(AuthorizationService);
  readonly permissions = Permissions;

  readonly statuses: BenefitPaymentStatus[] = ['SCHEDULED', 'OVERDUE', 'PAID', 'CANCELLED'];

  readonly payments = signal<BenefitPayment[]>([]);
  readonly scholarships = signal<Scholarship[]>([]);
  readonly selected = signal<BenefitPayment | null>(null);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly message = signal('');

  readonly filters = this.fb.group({
    status: [''],
    scholarshipId: [null as number | null],
    dueBefore: [''],
    search: [''],
  });

  readonly settleForm = this.fb.group({
    paidAmount: [null as number | null],
    paymentReference: [''],
    paidDate: [''],
    notes: [''],
  });

  ngOnInit(): void {
    this.load();
    this.scholarshipApi.getScholarships().subscribe({
      next: (scholarships) => this.scholarships.set(scholarships ?? []),
      error: () => this.scholarships.set([]),
    });
  }

  load(): void {
    const value = this.filters.getRawValue();
    this.loading.set(true);
    this.error.set('');

    this.api
      .getPaymentsForReview(
        (value.status as BenefitPaymentStatus) || null,
        value.scholarshipId ?? null,
        value.dueBefore || null,
        value.search ?? ''
      )
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (payments) => this.payments.set(payments ?? []),
        error: () => this.error.set('The payment queue could not be loaded.'),
      });
  }

  open(payment: BenefitPayment): void {
    this.error.set('');
    this.message.set('');
    this.selected.set(payment);
    this.settleForm.reset({
      paidAmount: payment.amount,
      paymentReference: payment.paymentReference ?? '',
      paidDate: '',
      notes: '',
    });
  }

  close(): void {
    this.selected.set(null);
  }

  /** Marks a scheduled or overdue payment as settled. */
  settle(): void {
    const payment = this.selected();
    if (!payment || this.busy()) {
      return;
    }

    const value = this.settleForm.getRawValue();
    this.busy.set(true);
    this.error.set('');
    this.message.set('');

    this.api
      .settlePayment(payment.id, {
        paidAmount: value.paidAmount ?? null,
        paymentReference: value.paymentReference || null,
        paidDate: value.paidDate || null,
        notes: value.notes || null,
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (updated) => {
          this.selected.set(updated);
          this.message.set(
            `Payment ${updated.referenceNumber} settled${updated.paidAmount ? ` (${updated.paidAmount})` : ''}.`
          );
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The payment could not be settled.'),
      });
  }

  cancel(payment: BenefitPayment): void {
    this.error.set('');
    this.message.set('');

    this.api.cancelPayment(payment.id).subscribe({
      next: () => {
        this.message.set(`Payment ${payment.referenceNumber} cancelled.`);
        this.close();
        this.load();
      },
      error: (err) => this.error.set(err?.error?.message || 'The payment could not be cancelled.'),
    });
  }

  canSettle(payment: BenefitPayment): boolean {
    return payment.status === 'SCHEDULED' || payment.status === 'OVERDUE';
  }

  statusClass(status: string): string {
    switch (status) {
      case 'PAID':
        return 'status status-approved';
      case 'OVERDUE':
        return 'status status-rejected';
      case 'CANCELLED':
        return 'status status-cancelled';
      default:
        return 'status status-pending';
    }
  }
}
